import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AgeVerification from '../components/AgeVerification';
import CookieConsent from '../components/CookieConsent';
import { Shield, Eye, Lock, Database, UserCheck, Mail } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-book-cream">
      <Header />
      <main className="book-section">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div className="w-16 h-16 bg-book-brown rounded-full flex items-center justify-center">
                <Shield className="h-8 w-8 text-book-cream" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-book-brown mb-2">
                  Privacy Policy
                </h1>
                <p className="text-lg text-book-brown-dark">
                  Your privacy and data protection are fundamental to our commitment to responsible gaming
                </p>
              </div>
            </div>
            <div className="bg-book-beige rounded-lg p-4 text-sm text-book-brown-dark">
              <strong>Last Updated:</strong> 16 December 2025
            </div>
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed mb-12">
            <p className="mb-6">
              At Brit Ember, we recognize that privacy and data protection represent cornerstone principles in the modern
              gaming industry. Our Privacy Policy outlines our comprehensive approach to safeguarding your personal
              information while delivering the premium gaming experiences you deserve. This policy reflects our
              commitment to transparency, compliance with UK data protection legislation, and respect for individual privacy rights.
            </p>

            <p className="mb-6">
              As a UK-based casino aggregator, we operate under the strict regulatory framework established by the
              Gambling Commission and the UK's data protection authorities. Our privacy practices are designed to
              exceed minimum legal requirements, ensuring that your personal data is handled with the utmost care
              and respect throughout your gaming journey.
            </p>
          </div>

          {/* Main Content Sections */}
          <div className="space-y-12">
            {/* Section 1 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Database className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Information We Collect</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      We collect information necessary to provide our casino aggregation services and ensure compliance
                      with regulatory requirements. This includes personal data you voluntarily provide and information
                      collected through your use of our platform.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Personal Information You Provide</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>Contact details (name, email address, phone number)</li>
                      <li>Account registration information</li>
                      <li>Communication preferences and feedback</li>
                      <li>Age verification data for responsible gaming compliance</li>
                      <li>Support request details and correspondence</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Information Collected Automatically</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>IP address and geographical location data</li>
                      <li>Device information and browser type</li>
                      <li>Website usage patterns and navigation data</li>
                      <li>Cookie and tracking technology data</li>
                      <li>Platform interaction metrics</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Regulatory Compliance Data</h3>
                    <p className="mb-4">
                      In compliance with UK gambling legislation and anti-money laundering regulations, we may collect
                      and process additional information required for age verification, identity confirmation, and
                      responsible gaming monitoring. This data is handled with strict confidentiality and used solely
                      for regulatory compliance purposes.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Eye className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">How We Use Your Information</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      Your personal information is used to deliver exceptional service, ensure regulatory compliance,
                      and maintain the highest standards of responsible gaming. Every data usage purpose serves our
                      commitment to providing safe, fair, and enjoyable gaming experiences.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Service Delivery</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>Platform account creation and management</li>
                      <li>Customer support and communication</li>
                      <li>Personalized gaming recommendations</li>
                      <li>Technical support and troubleshooting</li>
                      <li>Service improvement and feature development</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Regulatory Compliance</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>Age verification and identity confirmation</li>
                      <li>Anti-money laundering monitoring</li>
                      <li>Responsible gambling protection</li>
                      <li>Gambling Commission reporting requirements</li>
                      <li>Fraud prevention and security measures</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Experience Enhancement</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>Website performance optimization</li>
                      <li>User experience personalization</li>
                      <li>Content relevance and customization</li>
                      <li>Marketing communication preferences</li>
                      <li>Service quality monitoring and improvement</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Lock className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Data Security and Protection</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      Security represents our highest priority in data management. We employ military-grade encryption,
                      multi-layered security protocols, and continuous monitoring to ensure your personal information
                      remains protected against unauthorized access, cyber threats, and data breaches.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Technical Security Measures</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>256-bit SSL/TLS encryption for all data transmission</li>
                      <li>Advanced firewall protection and intrusion detection</li>
                      <li>Regular security audits and vulnerability assessments</li>
                      <li>Multi-factor authentication for administrative access</li>
                      <li>Encrypted data storage with access controls</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Data Handling Procedures</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>Strict access controls and authorization protocols</li>
                      <li>Regular staff training on data protection</li>
                      <li>Secure data disposal procedures</li>
                      <li>Incident response and breach notification protocols</li>
                      <li>Third-party vendor security assessments</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">International Data Transfers</h3>
                    <p className="mb-4">
                      When international data transfers are necessary for service provision, we ensure adequate protection
                      through standard contractual clauses, adequacy decisions, or other legally recognized safeguards.
                      All international transfers comply with UK data protection regulations and maintain equivalent
                      protection standards.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <UserCheck className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Your Rights and Choices</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      Under UK data protection law, you have comprehensive rights regarding your personal information.
                      We are committed to facilitating the exercise of these rights and providing transparent information
                      about our data processing activities.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Data Subject Rights</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Access Right</h4>
                        <p className="text-sm text-book-brown-dark">Request a copy of your personal data and information about how it's processed.</p>
                      </div>
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Rectification Right</h4>
                        <p className="text-sm text-book-brown-dark">Request correction of inaccurate or incomplete personal data.</p>
                      </div>
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Erasure Right</h4>
                        <p className="text-sm text-book-brown-dark">Request deletion of your personal data under certain circumstances.</p>
                      </div>
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Restriction Right</h4>
                        <p className="text-sm text-book-brown-dark">Request limitation of how your personal data is processed.</p>
                      </div>
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Portability Right</h4>
                        <p className="text-sm text-book-brown-dark">Receive your personal data in a structured, machine-readable format.</p>
                      </div>
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Objection Right</h4>
                        <p className="text-sm text-book-brown-dark">Object to processing based on legitimate interests or direct marketing.</p>
                      </div>
                    </div>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Contacting Us About Your Rights</h3>
                    <p className="mb-4">
                      To exercise any of these rights, please contact our Data Protection Officer using the information
                      provided below. We will respond to your request within 30 days and provide assistance throughout
                      the process. Please note that some requests may require identity verification to ensure data security.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Mail className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Cookies and Tracking Technologies</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      We use cookies and similar tracking technologies to enhance your browsing experience, analyze
                      website usage, and provide personalized content. Our cookie practices are designed to balance
                      functionality with privacy protection.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Types of Cookies We Use</h3>
                    <div className="space-y-4 mb-4">
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Essential Cookies</h4>
                        <p className="text-sm text-book-brown-dark">Required for website functionality, security, and basic operations. Cannot be disabled.</p>
                      </div>
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Analytics Cookies</h4>
                        <p className="text-sm text-book-brown-dark">Help us understand how visitors use our website to improve user experience.</p>
                      </div>
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Functional Cookies</h4>
                        <p className="text-sm text-book-brown-dark">Remember your preferences and settings for a personalized experience.</p>
                      </div>
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Marketing Cookies</h4>
                        <p className="text-sm text-book-brown-dark">Used to deliver relevant advertisements and track campaign effectiveness.</p>
                      </div>
                    </div>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Cookie Management</h3>
                    <p className="mb-4">
                      You can control cookie preferences through your browser settings or our cookie consent banner.
                      Please note that disabling certain cookies may affect website functionality and user experience.
                      For detailed information about cookie management, please refer to our Cookies Policy.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-book-beige rounded-xl p-8 shadow-book-inset">
              <h2 className="text-2xl font-bold text-book-brown mb-6 text-center">Contact Our Data Protection Team</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="text-center">
                  <h3 className="font-semibold text-book-brown mb-2">Data Protection Officer</h3>
                  <p className="text-book-brown-dark mb-1">dpo@britember.com</p>
                  <p className="text-sm text-book-brown-dark">For privacy-related inquiries and rights requests</p>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-book-brown mb-2">General Inquiries</h3>
                  <p className="text-book-brown-dark mb-1">privacy@britember.com</p>
                  <p className="text-sm text-book-brown-dark">For general privacy questions</p>
                </div>
              </div>
              <div className="mt-6 text-center">
                <p className="text-sm text-book-brown-dark">
                  <strong>Mailing Address:</strong> Brit Ember, London, United Kingdom
                </p>
                <p className="text-sm text-book-brown-dark mt-2">
                  We aim to respond to all privacy inquiries within 30 days.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <AgeVerification />
      <CookieConsent />
    </div>
  );
};

export default PrivacyPolicy;
